// Runtime configuration system for M247 Digital Signage
// This allows environment variables to be injected at deployment time

interface RuntimeConfig {
  VITE_AZURE_STORAGE_CONTAINER_NAME: string;
  VITE_PRAYER_SYNC_INTERVAL: string;
  VITE_DIGITAL_SIGNAGE_API_DOMAIN: string;
  VITE_ENVIRONMENT: string;
  VITE_DIGITAL_SIGNAGE_CONTAINER_NAME: string;
  VITE_STORAGE_URL: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const SAVED_CONFIG_KEY = "m247-runtime-config";

const loadSavedConfig = (): RuntimeConfig | null => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_CONFIG_KEY) || "null");
  } catch {
    return null;
  }
};

const saveConfig = (config: RuntimeConfig) => {
  try {
    localStorage.setItem(SAVED_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Private mode or full storage: the next load fetches it again.
  }
};

class ConfigManager {
  private config: RuntimeConfig | null = null;
  private initPromise: Promise<void> | null = null;

  initialize(): Promise<void> {
    // Reuse the in-flight promise so module-load + main.tsx don't double-fetch
    if (!this.initPromise) {
      this.initPromise = this.doInitialize().then(() => {
        if (this.config) {
          saveConfig(this.config);
          return;
        }
        // Offline before env.js was ever cached: the build-time placeholders would point
        // every image at a URL that isn't cached, so use the last config this device loaded.
        this.config = loadSavedConfig();
        // A failed load must not pin the screen to build-time placeholder
        // config for the whole session — the next caller retries
        if (!this.config) this.initPromise = null;
      });
    }
    return this.initPromise;
  }

  private async doInitialize(): Promise<void> {
    try {
      // First, try to get config from window (injected at runtime)
      if (window.__RUNTIME_CONFIG__) {
        this.config = window.__RUNTIME_CONFIG__;
        console.log("Using runtime config from window");
        return;
      }

      // Next, try to fetch from /config/env.js
      // This blocks first render (main.tsx awaits it), so it must never hang:
      // on timeout we fall back to build-time config
      const response = await fetch("/config/env.js", {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
        },
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        const configText = await response.text();

        // Check if we got HTML instead of JavaScript (common with SPAs that redirect everything to index.html)
        const contentType = response.headers.get("content-type") || "";
        const isHtml =
          contentType.includes("html") || configText.trim().startsWith("<");

        if (isHtml) {
          console.warn(
            "Received HTML instead of JavaScript for /config/env.js - likely file doesn't exist",
          );
        } else {
          try {
            // Execute the config script to set window.__RUNTIME_CONFIG__
            const script = document.createElement("script");
            script.textContent = configText;
            document.head.appendChild(script);
            document.head.removeChild(script);

            if (window.__RUNTIME_CONFIG__) {
              this.config = window.__RUNTIME_CONFIG__;
              console.log("Using runtime config from /config/env.js");
            } else {
              console.warn(
                "/config/env.js loaded but didn't set window.__RUNTIME_CONFIG__",
              );
            }
          } catch (scriptError) {
            console.warn("Failed to execute /config/env.js:", scriptError);
          }
        }
      } else {
        console.warn(
          `Failed to fetch /config/env.js: ${response.status} ${response.statusText}`,
        );
      }
    } catch (error) {
      console.warn(
        "Failed to load runtime config, falling back to build-time config:",
        error,
      );
    }
  }

  get(key: keyof RuntimeConfig): string {
    // Return runtime config if available, otherwise fall back to build-time config
    if (this.config && this.config[key]) {
      return this.config[key];
    }

    // Fall back to Vite's build-time environment variables
    return import.meta.env[key] || "";
  }

  get azureStorageContainerName(): string {
    return this.get("VITE_AZURE_STORAGE_CONTAINER_NAME");
  }

  get prayerSyncInterval(): number {
    return Number(this.get("VITE_PRAYER_SYNC_INTERVAL")) || 5 * 60 * 1000; // default 5 min
  }

  get digitalSignageApiDomain(): string {
    return this.get("VITE_DIGITAL_SIGNAGE_API_DOMAIN");
  }

  get isProduction(): boolean {
    return this.get("VITE_ENVIRONMENT").toLowerCase() === "production";
  }

  get isDevelopment(): boolean {
    return this.get("VITE_ENVIRONMENT").toLowerCase() === "development";
  }

  /**
   * Get the storage URL (Azure Blob Storage base URL)
   */
  get storageUrl(): string {
    // If VITE_STORAGE_URL is set, use it
    const storageUrl = this.get("VITE_STORAGE_URL");
    if (storageUrl) {
      return storageUrl.replace(/\/$/, "");
    }
    return "";
  }

  /**
   * Get the digital signage container name
   */
  get digitalSignageContainerName(): string {
    return this.get("VITE_DIGITAL_SIGNAGE_CONTAINER_NAME").replace(
      /^\/+|\/+$/g,
      "",
    ); // Remove leading/trailing slashes
  }

  /**
   * Build a full blob URL from a relative path
   * If the path is already absolute (starts with http), return as-is
   */
  getBlobUrl(path: string | null | undefined): string {
    if (!path || path.startsWith("http")) {
      return path || "";
    }
    const cleanPath = path.replace(/^\//, ""); // Remove leading slash
    return `${this.storageUrl}/${this.digitalSignageContainerName}/${cleanPath}`;
  }
}

// Create singleton instance
export const runtimeConfig = new ConfigManager();

// Initialize on module load
runtimeConfig.initialize();

// Also export a function to ensure initialization (for components that need it immediately)
export const ensureConfigLoaded = () => runtimeConfig.initialize();
